import React, {ReactNode} from 'react';
import {useDialogContext} from 'components/dialog/DialogContext';
import {useResourceContext} from 'components/record/ResourceContext';
import {useMutate} from 'hooks/useFetchData';
import {FormContainer, SubmitHandler} from 'react-hook-form-mui';
import {toast} from 'react-toastify';
import {RecordType, SingleRecord} from 'components/record/RecordContext';

const CreateDialogBody = ({
  children,
  defaultValues = {},
  onSuccess,
  many = false
}: {
  children: ReactNode;
  defaultValues?: SingleRecord;
  onSuccess?: () => void;
  many?: boolean;
}) => {
  const {resource, params} = useResourceContext();
  const {close} = useDialogContext();
  const [mutate] = useMutate<{item?: RecordType; items: RecordType[]}>(many ? 'CREATE_MANY' : 'CREATE_ONE', resource, {
    variables: params,
    context: {
      headers: {
        'apollo-require-preflight': true
      }
    }
  });

  const removeIdFromPayload = (obj: unknown): unknown => {
    if (Array.isArray(obj)) {
      return obj.map((item) => removeIdFromPayload(item));
    } else if (obj !== null && typeof obj === 'object') {
      // Check if it's a moment or dayjs object
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((obj as any)._isAMomentObject || (obj as any).$d) {
        return obj;
      }
      const newObj = {...obj} as Record<string, unknown>;
      delete newObj.id;
      return Object.keys(newObj).reduce((acc, key) => {
        if (key === 'file') {
          acc[key] = newObj[key];
        } else {
          acc[key] = removeIdFromPayload(newObj[key]);
        }
        return acc;
      }, {} as Record<string, unknown>);
    }
    return obj;
  };

  const handleCreate: SubmitHandler<React.FormEvent<HTMLFormElement>> = (payload) => {
    const cleanedPayload = removeIdFromPayload(payload);
    if (typeof mutate === 'function') {
      mutate({variables: {payload: cleanedPayload}})
        .then((res) => {
          let itemName;
          if (res.data?.item) {
            itemName = res.data?.item.name || res.data?.item.id || '';
          }
          if (res.data?.items) {
            itemName = res.data?.items[0]?.name || res.data?.items[0]?.id || '';
          }
          if (itemName) {
            toast.success(`${resource}${itemName} has been created successfully!`);
            if (onSuccess && typeof onSuccess === 'function') {
              onSuccess();
            }
            close();
          }
        })
        .catch((error) => {
          if (error.message) {
            toast.error(error.message);
          }
        });
    }
  };

  return (
    <FormContainer FormProps={{id: `create_${resource}`, noValidate: true}} onSuccess={handleCreate} defaultValues={defaultValues}>
      {children}
    </FormContainer>
  );
};

export default CreateDialogBody;
